import type ExtendedMap from '@/models/ExtendedMap'
import WebviewService from '@/service/webview.service'
import type { Iter, NullableObj, Obj, UnionToIntersection } from '@/types/utilityTypes'

import appPostMsg from '../appPostMsg'

export const openLink = (url: string) => {
  if (WebviewService.isAppAndroid) {
    appPostMsg.openBrowser(url)
  } else {
    window.open(url, '_blank')
  }
}

export const copyToClipboard = (text: string, onSuccess?: () => void, onFailed?: () => void) => {
  // Clipboard API - 구형 브라우저 미지원
  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(() => onSuccess?.())
      .catch(() => {
        onFailed?.()
      })
  } else {
    // QueryCommand API - 신형 브라우저 미지원
    if (!document.queryCommandSupported('copy')) {
      return onFailed?.()
    }

    const textarea = document.createElement('textarea')
    textarea.style.display = 'none'
    textarea.value = text

    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)

    onSuccess?.()
  }
}

// matchArr 배열 값대로 정렬시킴, matchArr에 없던 값은 맨 뒤로 이동
// ex) sortMatchArr(['q', 'a', 'o'])(['o', null, 'q')
// -> ['q', 'o', null]
export const sortMatchArr =
  <T>(matchArr: T[]) =>
  (arr: any[]) => {
    return arr.sort((a, b) => {
      const aIndex = matchArr.findIndex((v) => v === a)

      if (aIndex === -1) {
        return 1
      }

      const bIndex = matchArr.findIndex((v) => v === b)

      if (bIndex === -1) {
        return -1
      }

      return aIndex - bIndex
    })
  }
/* ex) const arr = [['a1', 'b1'], ['a2', 'b2']]
 * arr.map(tupleReverse) // [['b1', 'a1'], ['b2', 'a2']]
 * */
export const tupleReverse = <T1, T2>([v1, v2]: readonly [T1, T2]) => [v2, v1] as const

/* 값 min max 제한
 * ex) const confineValue = confineMinMax(1, 10)
 * confineValue(-2) // :1
 * confineValue(15) // 10
 * confineValue(7) // 7
 * */
export const confineMinMax = (min: number, max: number) => {
  return (value: number) => {
    if (value <= min) return min
    if (value >= max) return max
    return value
  }
}

/* subObj가 superObj의 부분집합인지
 * ex) isSubset({name: '김회준', age: 17, phone: '010-5300-3599'}, {name: '김회준', age: 17}) // true
 * isSubset({name: '김회준', age: 29}, {name: '김회준', age: 29}) // true
 * isSubset({ name: '김회준', age: 29 }, { name: '김회준', age: 30 }) false
 * */
export const isSubset = <T>(superObj: T, subObj: Partial<T>) => {
  for (const subObjKey in subObj) {
    const isSameField = subObj[subObjKey] === superObj[subObjKey]
    if (!isSameField) return false
  }

  return true
}

/* 컬렉션을 순회하며 omits의 값 중 부분집합을 만족하는 것들을 뺌
 * ex) const coll = [{a:10, b:5}, {a:32, b:7}, {a:10, b:9}, {a:16, b:42}, {a:3, b:7}]
 * const omits = [{b:7}, {a:12, b:42}, {a:10, b:9}]
 * omitCollection(coll, omits) //=> [{a:10, b:5}, {a:16, b:42}]
 * */
export const omitCollection = <S>(collection: S[], omits: Partial<S>[]): S[] => {
  const _omits = [...omits]

  return collection.filter((collectionItem) => {
    for (let i = 0; i < _omits.length; i++) {
      if (isSubset(collectionItem, _omits[i])) {
        // _omits.splice(i, 1)
        return false
      }
    }
    return true
  })
}

// ts4.0에서 지원하는 더 강력한 타입 체킹으로 변경
// Object.assign 대신 쓰시면 됩닌다.
export const typeSafeAssign = <T extends any[]>(...args: [...T]) =>
  args.reduce<UnionToIntersection<T[number]>>(
    (result, current) =>
      (Object.keys(current) as Array<keyof typeof current>).reduce((target, key) => {
        target[key] = current[key]
        return target
      }, result),
    args[0],
  )

// assign 할 값이 undefined 거나 null이면 overwrite하지 않음
export const assignExceptNullable = <T extends any[]>(...args: [...T]) =>
  args.reduce<UnionToIntersection<T[number]>>(
    (result, current) =>
      (Object.keys(current) as Array<keyof typeof current>).reduce((target, key) => {
        const currentValue = current[key]
        if (currentValue !== undefined && currentValue !== null) {
          target[key] = currentValue
        }
        return target
      }, result),
    args[0],
  )

// lodash _.pick이 타입 체킹이 너무 약해서 빡쳐서 만들습니다.
// _.pick 대신에 쓰시면 됩니다.
export const pick = <T extends Record<K[number], unknown>, K extends Readonly<Array<keyof T>>>(
  obj: T,
  ...props: [...K]
) => {
  const result = {} as Partial<T>

  for (const prop of props) {
    if (obj[prop]) result[prop] = obj[prop]
  }

  return result as Pick<T, K[number]>
}

type hasForEachType<T> = T[] | Map<unknown, T> | Set<T> | ExtendedMap<any, T>

/***
 * lodash의 GroupBy를 Set으로 만들고 싶을때. 받는 인자도 Map, Set, Array 다 되게 작성.
 * @example
 * setGroupBy([
 *  { id: 'I1', age: 14 },
 *  { id: 'I3', age: 16 },
 *  { id: 'I3', age: 18 },
 * ],
 * (v) => v.id
 * ) // => { I1: Set { { id: 'I1', age: 14 } }, I3: Set { { id: 'I3', age: 16 }, { id: 'I3', age: 18 } } }
 ***/
export function setGroupBy<
  valueT,
  fnT extends (value: valueT, index?: number, values?: hasForEachType<valueT>) => any,
>(list: hasForEachType<valueT>, keyGetter: fnT) {
  const obj = {} as { [key in ReturnType<fnT>]?: Set<valueT> }
  list.forEach((item, index, items) => {
    const key = keyGetter(item, index, items)
    if (key === undefined) return
    if (!obj[key]) {
      obj[key] = new Set<valueT>()
    }
    obj[key].add(item)
  })
  return obj
}
/***
 * null필드 삭제
 ***/
export function deleteNullField<T extends Obj>(obj: T): Partial<T> {
  const newObj: Partial<T> = { ...obj }
  for (const newObjKey in newObj) {
    if (newObj[newObjKey] === null) {
      delete newObj[newObjKey]
    }
  }
  return newObj
}
/***
 * 빈 string을 null로
 ***/
export function emptyStringToNull<T extends Obj>(obj: T): NullableObj<T> {
  const newObj: NullableObj<T> = { ...obj }
  for (const newObjKey in newObj) {
    if (newObj[newObjKey] === '') {
      newObj[newObjKey] = null
    }
  }
  return newObj
}

// key, keyIterator
export function getValueFromKeyIter<K extends keyof V, V>(
  value: V,
  keyIter: K | ((value: V) => V[K]),
) {
  if (typeof keyIter === 'function') {
    return keyIter(value)
  }
  return value[keyIter]
}

// Map 형태를 GroupBy 하고 Filtering 해서
// Map<GroupByKey, Set<Value>> 형태로 만듦
/* example) mapGroupFilterBy(new map({ group: '초1', name: 'aa', age:16 }, ...).values(), 'growup', (v) => v.age > 15)
 * -> Map('초1', Set{ { group: '초1', name: 'aa', age:16 }, ... })
 * */
export function mapGroupFilterBy<K extends keyof V, V>(
  values: IterableIterator<V>,
  groupByIter: Iter<K, V>,
  filterIter: (value: V) => boolean = () => true,
): Map<V[K], Set<V>> {
  const newMap = new Map<V[K], Set<V>>()
  for (const v of values) {
    // 각 아이템의 그룹 키를 가져옴
    const groupKeyForItem = getValueFromKeyIter(v, groupByIter)
    const set = newMap.get(groupKeyForItem)

    // 해당 그룹 키에 추가
    if (set && filterIter(v)) {
      set.add(v)
    } else {
      newMap.set(groupKeyForItem, new Set<V>([v]))
    }
  }
  return newMap
}
